import staffFetch from "./staffFetch";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const API = "https://live-evo.com/api";

function LiveActiveBadge() {
  const [count, setCount] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function refresh() {
      try {
        const token = localStorage.getItem("evoAccessToken") || "";
        const response = await fetch(
          "https://live-evo.com/api/staff/lives/active",
          {headers: {Authorization: `Bearer ${token}`}}
        );
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error("Compteur indisponible");
        }
        if (mounted) setCount(Number(data.count || 0));
      } catch {
        if (mounted) setCount(null);
      }
    }

    refresh();
    const interval = setInterval(refresh, 15000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  return (
    <span
      title="Lives actuellement en cours"
      style={{
        padding: "3px 7px",
        borderRadius: "10px",
        background: count > 0 ? "#ff315f" : "#21465b",
        color: "#fff",
        fontSize: "11px",
        fontWeight: 900
      }}>
      {count === null ? "—" : count > 99 ? "99+" : count}
    </span>
  );
}

function StaffDashboardPage() {
  const navigate = useNavigate();

  let connectedAccount = {};
  try {
    connectedAccount = JSON.parse(
      localStorage.getItem("currentUser") || "{}"
    );
  } catch {}

  const isAdmin =
    connectedAccount.is_admin === true ||
    connectedAccount.is_admin === 1 ||
    connectedAccount.role === "admin";

  const displayName =
    connectedAccount.role === "staff"
      ? "Staff EVO"
      : isAdmin
        ? "Administrateur EVO"
        : connectedAccount.pseudo ||
          connectedAccount.username ||
          "Utilisateur EVO";

  const [badges, setBadges] = useState({
    support: 0,
    requests: 0,
    videosReported: 0,
    messagesReported: 0,
    usersReported: 0,
    videosPending: 0,
    blockedComments: 0,
    privateMessageSpam: 0,
    usersBanned: 0,
    agencies: 0,
    streamAlerts: 0
  });

  const countOpen = (list) => {
    return list.filter((item) => {
      const status = String(item.status || item.report_status || "").toLowerCase();
      return (
        status === "" ||
        status === "nouveau" ||
        status === "ouvert" ||
        status === "open" ||
        status === "en_attente" ||
        status === "pending" ||
        status === "a_verifier"
      );
    }).length;
  };

  const loadBadges = async () => {
    try {
      const results = await Promise.allSettled([
        staffFetch(`${API}/support/getTickets`).then((r) => r.json()),
        staffFetch(`${API}/staff/requests`).then((r) => r.json()),
        staffFetch(`${API}/admin/reported-videos`).then((r) => r.json()),
        staffFetch(`${API}/staff/message-reports`).then((r) => r.json()),
        staffFetch(`${API}/staff/user-reports`).then((r) => r.json()),
        staffFetch(`${API}/staff/video-pending`).then((r) => r.json()),
        staffFetch(`${API}/staff/blocked-comments`).then((r) => r.json()),
        staffFetch(`${API}/staff/private-message-spam`).then((r) => r.json()),
        staffFetch(`${API}/staff/users-banned`).then((r) => r.json()),
        staffFetch(`${API}/agencies/staff/counts`, {
          headers: {
            Authorization: `Bearer ${
              localStorage.getItem("evoAccessToken") || ""
            }`
          }
        }).then((r) => r.json()),
        staffFetch(`${API}/staff/lives/alerts`, {
          headers: {
            Authorization: `Bearer ${
              localStorage.getItem("evoAccessToken") || ""
            }`
          }
        }).then((r) => r.json())
      ]);

      const getValue = (index, fallback) =>
        results[index].status === "fulfilled" ? results[index].value : fallback;

      const supportData = getValue(0, []);
      const requestsData = getValue(1, {});
      const videosReportsData = getValue(2, {});
      const messageReportsData = getValue(3, {});
      const userReportsData = getValue(4, {});
      const videosPendingData = getValue(5, {});
      const blockedCommentsData = getValue(6, {});
      const spamData = getValue(7, {});
      const bannedData = getValue(8, {});
      const agenciesData = getValue(9, {});
      const streamAlertsData = getValue(10, {});

      const supportList = Array.isArray(supportData) ? supportData : [];

      const requestsList = Array.isArray(requestsData?.requests)
        ? requestsData.requests
        : [];

      const videosReportsList = Array.isArray(videosReportsData?.reports)
        ? videosReportsData.reports
        : [];

      const messageReportsList = Array.isArray(messageReportsData?.reports)
        ? messageReportsData.reports
        : [];

      const userReportsList = Array.isArray(userReportsData?.reports)
        ? userReportsData.reports
        : [];

      const videosPendingList = Array.isArray(videosPendingData?.videos)
        ? videosPendingData.videos
        : [];

      const blockedCommentsList = Array.isArray(blockedCommentsData?.comments)
        ? blockedCommentsData.comments
        : [];

      const spamList = Array.isArray(spamData?.data) ? spamData.data : [];
      const bannedList = Array.isArray(bannedData?.data) ? bannedData.data : [];

      setBadges({
        support: countOpen(supportList),
        requests: countOpen(requestsList),
        videosReported: countOpen(videosReportsList),
        messagesReported: countOpen(messageReportsList),
        usersReported: countOpen(userReportsList),
        videosPending: videosPendingList.length,
        blockedComments: blockedCommentsList.length,
        privateMessageSpam: spamList.length,
        usersBanned: bannedList.length,
        agencies: Number(agenciesData?.total || 0),
        streamAlerts: Number(streamAlertsData?.count || 0)
      });
    } catch (err) {
      console.log("Erreur badges staff web:", err);
    }
  };

  useEffect(() => {
    loadBadges();

    const interval = setInterval(() => {
      loadBadges();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const sections = [
    {
      title: "EVO Agences",
      cards: [
        {
          title: "Demandes d’agences",
          icon: "🏢",
          path: "/staff/agencies",
          badge: badges.agencies
        }
      ]
    },
    {
      title: "Support utilisateurs",
      cards: [
        { title: "Tickets support", icon: "📩", path: "/staff/support", badge: badges.support },
        { title: "Demandes particulières", icon: "📝", path: "/staff/requests", badge: badges.requests },
        { title: "Réponses rapides", icon: "⚡", path: "/staff/replies" }
      ]
    },
    {
      title: "Signalements",
      cards: [
        { title: "Vidéos signalées", icon: "🎬", path: "/staff/reported-videos", badge: badges.videosReported },
        { title: "Lives signalés", icon: "🚨", path: "/staff/live-reports" },
        { title: "Messages privés", icon: "💌", path: "/staff/message-reports", badge: badges.messagesReported },
        { title: "Chat live", icon: "💬", path: "/staff/live-chat-reports" },
        { title: "Utilisateurs", icon: "👤", path: "/staff/user-reports", badge: badges.usersReported }
      ]
    },
    {
      title: "Modération contenu",
      cards: [
        { title: "Vidéos en attente", icon: "📂", path: "/staff/video-pending", badge: badges.videosPending },
        { title: "Commentaires bloqués", icon: "🗨️", path: "/staff/blocked-comments", badge: badges.blockedComments },
        { title: "Spam / bots", icon: "🤖", path: "/staff/spam-bots", badge: badges.privateMessageSpam },
        { title: "Comptes suspects", icon: "⚠️", path: "/staff/suspicious-accounts" },
        { title: "Users bannis", icon: "🚫", path: "/staff/users-banned", badge: badges.usersBanned },
        { title: "Contrôles sécurité", icon: "✔️", path: "/staff/security" }
      ]
    },
    {
      title: "Modération live",
      cards: [
        { title: "Lives actifs", icon: "📡", path: "/staff/lives-active" },
        { title: "Lives à risque", icon: "⚠️", path: "/staff/live-risk" },
        { title: "Stream alert", icon: "⚡", path: "/staff/stream-alert", badge: badges.streamAlerts },
        { title: "Historique live", icon: "📜", path: "/staff/live-history" }
      ]
    }
  ];

  return (
    <div style={{
      minHeight: "100vh",
      background: "radial-gradient(circle at top, #071a2b 0%, #020912 38%, #010409 100%)",
      color: "#fff"
    }}>
      <div style={{
        borderBottom: "1px solid rgba(0,242,255,.18)",
        background: "rgba(3,17,29,.94)",
        padding: "23px 20px",
        textAlign: "center"
      }}>
        <div style={{fontSize: "22px", fontWeight: 900, letterSpacing: "1px"}}>
          🛡 CENTRE STAFF EVO
        </div>
        <div style={{
          color: "#00eaff",
          fontSize: "10px",
          letterSpacing: "2px",
          fontWeight: 800,
          marginTop: "5px"
        }}>
          EVO CONTROL CENTER
        </div>
        <div style={{
          marginTop: "9px",
          color: "#afc8d8",
          fontSize: "12px",
          fontWeight: 700
        }}>
          Connecté : {displayName}
        </div>
      </div>

      <div style={{
        width: "min(1180px, calc(100% - 32px))",
        margin: "0 auto",
        padding: "26px 0 56px"
      }}>
        {sections.map(section => (
          <section key={section.title} style={{marginBottom: "29px"}}>
            <h2 style={{
              margin: "0 0 5px",
              fontSize: "17px",
              fontWeight: 900
            }}>
              {section.title}
            </h2>
            <div style={{
              color: "#7895a9",
              fontSize: "11px",
              marginBottom: "13px"
            }}>
              Accès aux outils de {section.title.toLowerCase()}.
            </div>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
              gap: "10px"
            }}>
              {section.cards.map(card => (
                <button
                  key={card.title}
                  type="button"
                  onClick={() => navigate(card.path)}
                  style={{
                    minHeight: "86px",
                    padding: "13px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    position: "relative",
                    textAlign: "left",
                    cursor: "pointer",
                    borderRadius: "13px",
                    border: "1px solid rgba(0,234,255,.22)",
                    background: "linear-gradient(145deg, #0a2031, #05111e)",
                    color: "#fff",
                    boxShadow: "0 6px 20px rgba(0,0,0,.18)"
                  }}
                >
                  <span style={{
                    width: "42px",
                    height: "42px",
                    flex: "0 0 42px",
                    display: "grid",
                    placeItems: "center",
                    borderRadius: "11px",
                    background: "#102b41",
                    border: "1px solid #1c4c66",
                    fontSize: "21px"
                  }}>
                    {card.icon}
                  </span>
                  <span style={{flex: 1, minWidth: 0}}>
                    <span style={{display: "block", fontSize: "13px", fontWeight: 800}}>
                      {card.title}
                    </span>
                    <span style={{display: "block", color: "#7595a9", fontSize: "10px", marginTop: "4px"}}>
                      Ouvrir l’espace
                    </span>
                  </span>
                  {card.path === "/staff/lives-active" && <LiveActiveBadge />}
                  {Number(card.badge || 0) > 0 && (
                    <span style={{
                      padding: "3px 7px",
                      borderRadius: "10px",
                      background: "#ff315f",
                      color: "#fff",
                      fontSize: "11px",
                      fontWeight: 900
                    }}>
                      {card.badge > 99 ? "99+" : card.badge}
                    </span>
                  )}
                  <span style={{color: "#00eaff", fontSize: "20px"}}>›</span>
                </button>
              ))}
            </div>
          </section>
        ))}

        <div className="text-center mt-4">
          <button
            onClick={async () => {
              const token = localStorage.getItem("evoAccessToken");
              let account = {};

              try {
                account = JSON.parse(
                  localStorage.getItem("currentUser") || "{}"
                );
              } catch {}

              if (token && account.role === "staff") {
                try {
                  const response = await staffFetch(
                    "/api/auth/staff/logout",
                    {
                      method: "POST",
                      headers: {
                        Authorization: `Bearer ${token}`
                      }
                    }
                  );

                  if (!response.ok && response.status !== 401) {
                    alert(
                      "La déconnexion Staff a échoué. Réessaie."
                    );
                    return;
                  }
                } catch {
                  alert(
                    "Serveur indisponible : déconnexion Staff non confirmée."
                  );
                  return;
                }
              }

              localStorage.removeItem("evoAccessToken");
              localStorage.removeItem("currentUser");
              window.location.replace("/staff");
            }}
            style={{
              background: "#050505",
              color: "#00f2ff",
              border: "1px solid #00f2ff",
              borderRadius: "15px",
              padding: "12px 30px",
              fontWeight: "bold"
            }}
          >
            SE DÉCONNECTER
          </button>
        </div>
      </div>
    </div>
  );
}

export default StaffDashboardPage;